import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { UserBenefitView, UserBenefitInput, CompanyBenefitTypeView } from "../../../../models/payroll";
import { getUserBenefits, addUserBenefit, editUserBenefit, deleteUserBenefit, getCompanyBenefitTypes } from "../../../../api/Payroll/benefit";
import UserProfile from "../..";
import UserPayroll from "..";
import UserBenefitForm from "./userBenefitForm";
import { formatNumber, formatDate } from "../../../../utils";
import Pagination from "../../../../components/Pagination";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from "../../../../components/ui/Dialog";
import Button from "../../../../components/ui/Button";
import AppIcon from "../../../../components/AppIcon";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { Spinner } from "../../../../components/ui/Spinner";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/redux/store/store";
import { useComplianceCountry } from "../../../../hooks/useComplianceCountry";
import { getUser } from "../../../../api/General/account";
import CchiPolicySection from "../../components/CchiPolicySection";

type Props = Readonly<{
  t: any;
}>;

const UserBenefits = (props: Props) => {
  const { t } = props;
  const { id } = useParams();
  const { toast, showToast, dismissToast } = useToast();

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [count, setCount] = useState(10);
  const [offset, setOffset] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [benefits, setBenefits] = useState<UserBenefitView[]>([]);
  const [benefitId, setBenefitId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedBenefitName, setSelectedBenefitName] = useState("");
  const [companyBenefitTypes, setCompanyBenefitTypes] = useState<CompanyBenefitTypeView[]>([]);
  const [currentBenefit, setCurrentBenefit] = useState<UserBenefitInput | undefined>({userId: id ?? ""} as UserBenefitInput);

  const [isSaudi, setIsSaudi] = useState<boolean | null>(null);
  const complianceCountry = useComplianceCountry();
  const loggedInUserId = useSelector((state: RootState) => state.user.userId);
  const role = useSelector((state: RootState) => state.user.role);
  const location = useLocation();
  const showCchi = complianceCountry === 'KSA' && isSaudi === false;
  // Benefits management is HR-owned; Admin is a company admin, not HR (see CLAUDE.md Role Model). An Admin
  // viewing their OWN profile falls through to the employee self-view below (they are also an employee).
  const isHr = role === 'Hr';

  useEffect(() => {
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSaudi === null || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isSaudi]);

  const loadAll = useCallback(async () => {
    await Promise.all([getUserBenefitsData(), getCompanyBenefitTypesData()]);
    if (complianceCountry === 'KSA' && id) {
      try {
        const userRes = await getUser(id);
        if (!userRes.errors?.length) {
          setIsSaudi(!!userRes.payload?.saudiNationalIdNumber);
        }
      } catch { /* keep null — section won't render */ }
    }
  }, []);

  const getUserBenefitsData = async () => {
    try {
      setLoading(true);
      const benefits = await getUserBenefits(id ?? "");
      setBenefits(benefits.payload);
    } catch (error) {
      console.log("Error", error);
      showToast(t("Failed to load user benefits"), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCompanyBenefitTypesData = async () => {
    try {
      const companyBenefitTypes = await getCompanyBenefitTypes();
      setCompanyBenefitTypes(companyBenefitTypes.payload);
    } catch (error) {
      console.log("Error", error);
      showToast(t("Failed to load benefit types"), 'error');
    }
  };

  const handleClickBenefitOpen = () => {
    setAddDialogOpen(true);
    setCurrentBenefit({userId: id ?? ""} as UserBenefitInput);
  };

  const handleClickBenefitClose = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
  };

  const handleAddBenefit = async (input: UserBenefitInput) => {
    setAddDialogOpen(false);
    await addBenefit(input);
    await loadAll();
  };

  const addBenefit = async (input: UserBenefitInput) => {
    try {
      setLoading(true);
      await addUserBenefit(input);
      showToast(t("Benefit added successfully"), 'success');
    } catch (error: any) {
      console.log("Error adding benefit:", error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
      if (!error.errors) showToast(t("Failed to add benefit"), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpenEditDialog = (benefit: UserBenefitView) => {
    setEditDialogOpen(true);
    setCurrentBenefit({
      id: benefit.id,
      userId: benefit.userId,
      companyBenefitTypeId: benefit.companyBenefitTypeId,
      effectiveDate: benefit.effectiveDate,
      expiryDate: benefit.expiryDate,
    });
  };

  const handleEditBenefit = async (input: UserBenefitInput) => {
    setEditDialogOpen(false);
    await editBenefit(input);
    await loadAll();
  };

  const editBenefit = async (input: UserBenefitInput) => {
    try {
      setLoading(true);
      await editUserBenefit(input.id ?? 0, input);
      showToast(t("Benefit updated successfully"), 'success');
    } catch (error: any) {
      console.log("Error editing benefit:", error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
      if (!error.errors) showToast(t("Failed to update benefit"), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpenDeleteDialog = async (benefitName: string, id: number) => {
    setDeleteDialogOpen(true);
    setSelectedBenefitName(benefitName);
    setBenefitId(id);
  };

  const handleClickCloseDeleteDialog = async () => {
    setDeleteDialogOpen(false);
    await loadAll();
  };

  const deleteBenefit = async (benefitId: number) => {
    try {
      setLoading(true);
      await deleteUserBenefit(benefitId);
      loadAll();
      setDeleteDialogOpen(false);
      showToast(t("Benefit deleted"), 'success');
    } catch (error: any) {
      console.log("Error deleting benefit:", error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
      if (!error.errors) showToast(t("Failed to delete benefit"), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
        <UserPayroll>
          <div className="flex flex-col gap-5">
            {isHr && (<>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary">
                {t("User Benefits")}
              </h3>
              <Button
                variant="default"
                onClick={handleClickBenefitOpen}
                iconName="Plus"
              >
                {t("Add Benefit")}
              </Button>
            </div>

            <div className="relative bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table aria-label={t("User Benefits Table") as string}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Benefit Type")}</TableHead>
                      <TableHead>{t("Price")}</TableHead>
                      <TableHead>{t("Effective Date")}</TableHead>
                      <TableHead>{t("Expiry Date")}</TableHead>
                      <TableHead aria-label={t("Actions") as string} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {benefits.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                            <p>{t("No benefits found")}</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {benefits
                      .slice(offset, Math.min(offset + count, benefits.length))
                      .map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="font-semibold">
                            {row.companyBenefitType?.name}
                          </TableCell>
                          <TableCell>
                            {row.companyBenefitType?.price
                              ? `${formatNumber(row.companyBenefitType?.price)} ${row.companyBenefitType?.currency?.currencyCode}`
                              : ''}
                          </TableCell>
                          <TableCell>{formatDate(new Date(row.effectiveDate))}</TableCell>
                          <TableCell>{row.expiryDate ? formatDate(new Date(row.expiryDate)) : t("No Expiry")}</TableCell>
                          <TableCell className="w-[90px]">
                            <div className="flex items-center gap-1">
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={t("Edit Benefit") as string}
                                      onClick={() => handleClickOpenEditDialog(row)}
                                    >
                                      <AppIcon name="Pencil" size={16} />
                                    </Button>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {t("Edit")}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                              <Tooltip.Provider>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={t("Delete Benefit") as string}
                                      onClick={() => handleClickOpenDeleteDialog(row.companyBenefitType?.name ?? '', row.id)}
                                    >
                                      <AppIcon name="Trash2" size={16} />
                                    </Button>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md border border-border" sideOffset={5}>
                                      {t("Delete")}
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {loading && (
                <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                  <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}
            </div>

            <Pagination
              currentPage={selectedPageIndex + 1}
              totalPages={Math.ceil(benefits.length / count) || 1}
              onPageChange={(newPage) => {
                setSelectedPageIndex(newPage - 1);
                setOffset((newPage - 1) * count);
              }}
            />

            <UserBenefitForm
              open={addDialogOpen || editDialogOpen}
              onClose={handleClickBenefitClose}
              onSave={addDialogOpen ? handleAddBenefit : handleEditBenefit}
              benefit={currentBenefit}
              companyBenefitTypes={companyBenefitTypes}
              t={t}
            />

            <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && handleClickCloseDeleteDialog()}>
              <DialogContent size="sm">
                <DialogHeader>
                  <DialogTitle>{t("Delete Benefit")}</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <p className="text-sm text-foreground">
                    {t("Are you sure you want to delete the benefit")}{' '}
                    <strong>{selectedBenefitName}</strong>?
                  </p>
                </DialogBody>
                <DialogFooter>
                  <Button
                    variant="destructive"
                    onClick={() => deleteBenefit(benefitId)}
                  >
                    {t("Delete")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClickCloseDeleteDialog}
                  >
                    {t("Cancel")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>)}

            <ToastRenderer toast={toast} onDismiss={dismissToast} />

            {!isHr && complianceCountry === 'KSA' && isSaudi === null && (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" />
              </div>
            )}

            {showCchi && (
              <div id="cchi" className="mt-6 scroll-mt-20">
                <CchiPolicySection
                  userId={id ?? ''}
                  isSaudi={false}
                  canEdit={isHr}
                />
              </div>
            )}
          </div>
        </UserPayroll>
      </UserProfile>
    </div>
  );
};

export default withTranslation()(UserBenefits as any) as any;
